import { useEffect, useState } from "react";
import { Bookmark, Plus, Trash2, Star } from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../components/UI/Popover";
import { Button } from "../../../../components/UI/Button";
import { cn } from "../../../../utils/utils";
import savedFilterViewApi from "../../../../api/savedFilterViewApi";

/**
 * Dropdown that lists the current staff's saved filter views, lets them:
 * - Apply a saved view (loads filter combo)
 * - Save the current filter state as a new view
 * - Delete a view
 * - Pin a view as the default (auto-applied on login)
 */
const SavedViewsMenu = ({
  staffId,
  accountId,
  currentFilterSnapshot,
  role,
  onApplyView,
}) => {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveMode, setSaveMode] = useState(false);
  const [nameInput, setNameInput] = useState("");

  const loadViews = async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await savedFilterViewApi.getByStaffId(staffId);
      if (res?.status === 1) setViews(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staffId]);

  const handleSave = async () => {
    const name = nameInput.trim();
    if (!name || !staffId || !accountId) return;
    const res = await savedFilterViewApi.create({
      staffId,
      accountId,
      name,
      role,
      filterJson: currentFilterSnapshot,
      sortMode: currentFilterSnapshot.sortMode || "recent",
    });
    if (res?.status === 1) {
      setNameInput("");
      setSaveMode(false);
      loadViews();
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Xóa view này?")) return;
    await savedFilterViewApi.deleteById(id);
    loadViews();
  };

  const handleToggleDefault = async (view, e) => {
    e.stopPropagation();
    // Unset any existing default first, then set this one.
    await Promise.all(
      views
        .filter((v) => v.isDefault && v._id !== view._id)
        .map((v) =>
          savedFilterViewApi.updateById(v._id || v.id, { isDefault: false }),
        ),
    );
    await savedFilterViewApi.updateById(view._id || view.id, {
      isDefault: !view.isDefault,
    });
    loadViews();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1 h-7 px-2 rounded-full border border-gray-200 bg-white text-[12px] font-medium text-gray-700 hover:bg-gray-50"
          title="Views đã lưu"
        >
          <Bookmark size={12} />
          View
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 bg-white" side="bottom" align="end">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Views của tôi
            </div>
            {!saveMode && (
              <button
                onClick={() => setSaveMode(true)}
                className="inline-flex items-center gap-0.5 text-[11px] text-primary hover:underline"
              >
                <Plus size={12} />
                Lưu view
              </button>
            )}
          </div>

          {saveMode && (
            <div className="border border-gray-200 rounded-md p-2 space-y-2">
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="Tên view..."
                autoFocus
                className="w-full text-[12px] border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
              <div className="flex justify-end gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 text-[11px]"
                  onClick={() => {
                    setSaveMode(false);
                    setNameInput("");
                  }}
                >
                  Hủy
                </Button>
                <Button
                  size="sm"
                  className="h-6 text-[11px]"
                  onClick={handleSave}
                  disabled={!nameInput.trim()}
                >
                  Lưu
                </Button>
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {loading && (
              <div className="px-2 py-2 text-[12px] text-gray-500">
                Đang tải...
              </div>
            )}
            {!loading && views.length === 0 && !saveMode && (
              <div className="px-2 py-2 text-[12px] text-gray-500 text-center">
                Chưa có view nào
              </div>
            )}
            {views.map((v) => (
              <button
                key={v._id || v.id}
                onClick={() => {
                  onApplyView?.(v);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-1 px-2 py-1.5 rounded-md text-[12px] hover:bg-gray-100 text-left group"
              >
                <span className="flex-1 truncate">{v.name}</span>
                {v.role && (
                  <span className="text-[10px] text-gray-400 uppercase">
                    {v.role}
                  </span>
                )}
                <button
                  onClick={(e) => handleToggleDefault(v, e)}
                  className={cn(
                    "p-0.5 rounded hover:bg-gray-200",
                    v.isDefault ? "text-yellow-500" : "text-gray-300",
                  )}
                  title={v.isDefault ? "Bỏ mặc định" : "Đặt mặc định"}
                >
                  <Star
                    size={12}
                    fill={v.isDefault ? "currentColor" : "none"}
                  />
                </button>
                <button
                  onClick={(e) => handleDelete(v._id || v.id, e)}
                  className="p-0.5 rounded text-gray-400 hover:bg-gray-200 hover:text-red-500 opacity-0 group-hover:opacity-100"
                  title="Xóa"
                >
                  <Trash2 size={12} />
                </button>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SavedViewsMenu;
